import { Component } from '@angular/core';
import { IDoesFilterPassParams, IFilterParams } from 'ag-grid-community';
import { AgFilterComponent } from 'ag-grid-angular';

@Component({
  selector: 'app-collection-filter',
  standalone: false,
  styles: `
    .wrap {
      padding: 12px 0;
    }

    .option-grid {
      padding: 4px 8px;
    }

    .option-grid label {
      height: 20px;
      display: flex;
      gap: 8px;
      align-items: center;
    }
  `,
  template: `
    <div class="wrap">
      <!-- Blank option -->
      <div class="option-grid">
        <label>
          <input type="checkbox"
            [checked]="blankSelected"
            (change)="onToggleBlank($event)" />
          <span><b>Em branco</b></span>
        </label>
      </div>
    
      <!-- Select / deselect all -->
      <div class="option-grid">
        <label>
          <input type="checkbox"
            [checked]="isAllSelected()"
            (change)="onToggleSelectAll($event)" />
          <span><b>Marcar todos</b></span>
        </label>
      </div>
    
      <!-- Individual values -->
      @for (value of allValues; track value) {
        <div class="option-grid">
          <label>
            <input type="checkbox"
              [value]="value"
              (change)="onCheckboxChange($event)"
              [checked]="selectedValues.has(value)" />
            <span>{{ getLabel(value) }}</span>
          </label>
        </div>
      }
    </div>
    `
})
export class AggridCollectionFilterComponent implements AgFilterComponent {
  private static readonly DIGITAL_STATUS_VALUES_ALLOWED = new Set([
    'idade nao elegivel',
    'transgenero',
    'gestante',
    'recusou realizar processo',
    'aguardando recebimento dos exames solicitados',
    'paciente encaminhado',
    'nada pendente',
    'aguardando preenchimento do questionario'
  ]);
  private static readonly STATUS_VALUES_EXCLUDED = new Set([
    'aguardando acolhimento a mulher',
    'aguardando acolhimento pre-encaminhamento',
    'aguardando encaminhamentos',
    'aguardando guia de exames',
    'aguardando leitura dos exames previos',
    'aguardando pedido de exames',
    'encaminhamentos ok',
    'exames solicitados ok',
    'questionarios ok',
  ]);
  private static readonly REFERRAL_VALUES_ALLOWED = new Set([
    'clinica medica',
    'atencao primaria',
    'cardiologia',
    'psiquiatria',
    'endocrinologia e metabolismo',
    'reumatologia',
    'ginecologia e obstetricia',
    'urologia',
    'pneumologia',
    'cirurgia vascular',
    'infectologia',
    'vacinacao',
    'ubs',
    'nefrologia',
    'gastroenterologia',
    'oftalmologia',
    'oncologia clinica',
    'mastologia',
    'coloproctologia',
  ]);

  /** ag-Grid passes the column + grid context here. */
  params!: IFilterParams;

  /** Unique set of values shown as checkboxes (excluding blanks). */
  allValues: string[] = [];

  /** Values currently ticked by the user. */
  selectedValues = new Set<string>();

  /** Optional dictionary for “value → friendly label”. */
  choices: Record<string, string> = {};

  /** Flag for the special “blank / empty” checkbox. */
  blankSelected = false;

  // ────────────────────────────────────────────────────────────────
  //  Life-cycle & ag-Grid contract
  // ────────────────────────────────────────────────────────────────

  /**
   * Called once when the filter is created.
   * – Gathers all distinct values from the column data and `choices`.
   * – Sorts and stores them in `allValues` for rendering.
   */
  agInit(params: IFilterParams): void {
    this.params = params;
    const field = params.colDef.field!;
    const uniques = new Set<string>();

    this.choices = params.colDef.filterParams?.choices || {};

    // Collect values present in the grid rows
    params.api.forEachNode(node => {
      const value = node.data?.[field];
      if (Array.isArray(value)) value.forEach(v => uniques.add(v));
      else if (typeof value === 'string') uniques.add(value);
    });

    // Collect any extra values supplied via `choices`
    Object.keys(this.choices).forEach(k => uniques.add(k));

    // Store a sorted array for template iteration
    this.allValues = Array.from(uniques)
      .filter(value => !this.shouldExcludeValue(value))
      .sort((a, b) => a.localeCompare(b));
  }

  /**
   * ag-Grid uses this to know if the filter is currently active.
   * Returns **true** if at least one value is selected
   * or if the “blank” checkbox is checked.
   */
  isFilterActive(): boolean {
    return this.blankSelected || this.selectedValues.size > 0;
  }

  /**
   * Core filtering logic executed for every row that ag-Grid evaluates.
   * Returns **true** when the row matches the selected criteria.
   */
  doesFilterPass(params: IDoesFilterPassParams): boolean {
    const field = this.params.colDef.field!;
    const value = params.data?.[field];

    // 1) Blank / empty match
    if ((value === null || value === undefined || value === '' || value?.length === 0) && this.blankSelected) {
      return true;
    }

    // 2) Match against selected non-blank values
    if (Array.isArray(value)) {
      return value.some(v => this.selectedValues.has(v));
    }
    if (typeof value === 'string') {
      return this.selectedValues.has(value);
    }

    return false;
  }

  /**
   * Persists the current state so ag-Grid can store or serialise it.
   */
  getModel(): any {
    if (!this.isFilterActive()) {
      return null;
    }
    return {
      values: Array.from(this.selectedValues),
      blankSelected: this.blankSelected,
    };
  }

  /**
   * Restores a saved model (called when the grid initialises
   * or when `api.setFilterModel()` is used).
   */
  setModel(model: any): void {
    if (!model) {
      this.blankSelected = false;
      this.selectedValues.clear();
      return;
    }

    this.blankSelected = model?.type === 'blank' || !!model?.blankSelected;
    this.selectedValues = new Set((model?.values || []).filter(value => !this.shouldExcludeValue(value)));
  }

  /** Optional cleanup hook — nothing to dispose for now. */
  destroy(): void {}

  // ────────────────────────────────────────────────────────────────
  //  Helpers used only by the template / UI
  // ────────────────────────────────────────────────────────────────

  /** Human-friendly label for each value (falls back to raw string). */
  getLabel(value: string): string {
    return this.choices?.[value] || value;
  }

  /** Toggles the “blank / empty” checkbox. */
  onToggleBlank(event: Event): void {
    this.blankSelected = (event.target as HTMLInputElement).checked;
    this.params.filterChangedCallback();
  }

  /** Handles an individual value checkbox click. */
  onCheckboxChange(event: Event): void {
    const cb = event.target as HTMLInputElement;
    cb.checked ? this.selectedValues.add(cb.value) : this.selectedValues.delete(cb.value);
    this.params.filterChangedCallback();
  }

  /** “Select / deselect all” behaviour (ignores blank option). */
  onToggleSelectAll(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    checked ? this.allValues.forEach(v => this.selectedValues.add(v))
      : this.selectedValues.clear();
    this.params.filterChangedCallback();
  }

  /** Used by the template to keep the “select all” checkbox in sync. */
  isAllSelected(): boolean {
    return this.allValues.length > 0 && this.selectedValues.size === this.allValues.length;
  }

  private shouldExcludeValue(value: string): boolean {
    const normalizedLabel = this.normalizeValue(this.getLabel(value));

    if (this.isDigitalStatusField()) {
      return !AggridCollectionFilterComponent.DIGITAL_STATUS_VALUES_ALLOWED.has(normalizedLabel);
    }

    if (this.isDigitalStatusForcedField()) {
      return AggridCollectionFilterComponent.STATUS_VALUES_EXCLUDED.has(normalizedLabel);
    }

    if (this.isReferralField()) {
      return !AggridCollectionFilterComponent.REFERRAL_VALUES_ALLOWED.has(normalizedLabel);
    }

    return false;
  }

  private isDigitalStatusField(): boolean {
    const field = this.params?.colDef?.field;
    return field === 'digitalStatus';
  }

  private isDigitalStatusForcedField(): boolean {
    const field = this.params?.colDef?.field;
    return field === 'digitalStatusForced';
  }

  private isReferralField(): boolean {
    const field = this.params?.colDef?.field;
    return field === 'referrals';
  }

  private normalizeValue(value: string): string {
    return (value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }
}
